import React, { Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteLoading from "@/components/RouteLoading";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import Login from "@/pages/Login";
import { PrivacyPage, TermsPage, LegalPage, CookiesPage, DPAPage, LicensesPage } from "@/pages/Legal";
import CookieBanner from "@/components/CookieBanner";
import FeedbackWidget from "@/components/FeedbackWidget";
import SupportChat from "@/components/SupportChat";
import InstallPrompt from "@/components/InstallPrompt";
import CommandPalette from "@/components/CommandPalette";
import AgentWidget from "@/components/AgentWidget";
import Landing from "@/pages/Landing";
import TrialBanner from "@/components/TrialBanner";
import { useAuth } from "@/contexts/AuthContext";

const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const Clients = React.lazy(() => import("@/pages/Clients"));
const Quotes = React.lazy(() => import("@/pages/Quotes"));
const QuoteEdit = React.lazy(() => import("@/pages/QuoteEdit"));
const Projects = React.lazy(() => import("@/pages/Projects"));
const Invoices = React.lazy(() => import("@/pages/Invoices"));
const Settings = React.lazy(() => import("@/pages/Settings"));
const Templates = React.lazy(() => import("@/pages/Templates"));
const Materials = React.lazy(() => import("@/pages/Materials"));
const Distributors = React.lazy(() => import("@/pages/Distributors"));
const OnboardingWizard = React.lazy(() => import("@/pages/OnboardingWizard"));
const PublicQuote = React.lazy(() => import("@/pages/PublicQuote"));
const PublicInvoice = React.lazy(() => import("@/pages/PublicInvoice"));
const PublicProject = React.lazy(() => import("@/pages/PublicProject"));
const HistoricalPortal = React.lazy(() => import("@/pages/HistoricalPortal"));
const Billing = React.lazy(() => import("@/pages/Billing"));
const Members = React.lazy(() => import("@/pages/Members"));
const AcceptInvite = React.lazy(() => import("@/pages/AcceptInvite"));
const MagicCallback = React.lazy(() => import("@/pages/MagicCallback"));
const Expenses = React.lazy(() => import("@/pages/Expenses"));
const Help = React.lazy(() => import("@/pages/Help"));
const Measurements = React.lazy(() => import("@/pages/Measurements"));
const MeasurementEdit = React.lazy(() => import("@/pages/MeasurementEdit"));
const ClientDetail = React.lazy(() => import("@/pages/ClientDetail"));
const Incidents = React.lazy(() => import("@/pages/Incidents"));
const Agenda = React.lazy(() => import("@/pages/Agenda"));
const Tax303 = React.lazy(() => import("@/pages/Tax303"));
const Reconcile = React.lazy(() => import("@/pages/Reconcile"));
const InternalCRM = React.lazy(() => import("@/pages/InternalCRM"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <ScrollToTop />
      <TrialBanner />
      <Suspense fallback={<RouteLoading />}>
        <RouteErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Login />} />
          <Route path="/sign/:token" element={<PublicQuote />} />
          <Route path="/pay/:token" element={<PublicInvoice />} />
          <Route path="/parte/:token" element={<PublicProject />} />
          <Route path="/legal/privacidad" element={<PrivacyPage />} />
          <Route path="/legal/terminos" element={<TermsPage />} />
          <Route path="/legal/aviso" element={<LegalPage />} />
          <Route path="/legal/cookies" element={<CookiesPage />} />
          <Route path="/legal/dpa" element={<DPAPage />} />
          <Route path="/legal/licencias" element={<LicensesPage />} />
          <Route path="/historico/:token" element={<HistoricalPortal />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/auth/magic" element={<MagicCallback />} />
          <Route path="/" element={<RootRoute />} />
          <Route path="/onboarding" element={<ProtectedRoute skipOnboardingGuard><OnboardingWizard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
          <Route path="/measurements" element={<ProtectedRoute><Measurements /></ProtectedRoute>} />
          <Route path="/measurements/:id" element={<ProtectedRoute><MeasurementEdit /></ProtectedRoute>} />
          <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
          <Route path="/quotes/:id" element={<ProtectedRoute><QuoteEdit /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
          <Route path="/materials" element={<ProtectedRoute><Materials /></ProtectedRoute>} />
          <Route path="/distributors" element={<ProtectedRoute><Distributors /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
          <Route path="/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
          <Route path="/tax/303" element={<ProtectedRoute><Tax303 /></ProtectedRoute>} />
          <Route path="/tax" element={<Navigate to="/tax/303" replace />} />
          <Route path="/reconcile" element={<ProtectedRoute><Reconcile /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/members" element={<ProtectedRoute><Members /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          {/* CRM interno de Factlify — sin enlace en navegación; el backend
              devuelve 403 a no-admins y la página redirige a /dashboard. */}
          <Route path="/admin/crm" element={<ProtectedRoute><InternalCRM /></ProtectedRoute>} />
          <Route path="/ayuda" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </RouteErrorBoundary>
      </Suspense>
      <CookieBanner />
      <FeedbackWidget />
      <SupportChat />
      <InstallPrompt />
      <CommandPalette />
      <AgentWidget />
    </>
  );
}

function AppShell() {
  const [toastPos, setToastPos] = React.useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches ? "top-right" : "top-center",
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = () => setToastPos(mq.matches ? "top-right" : "top-center");
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return (
    <>
      <AppRouter />
      <Toaster position={toastPos} toastOptions={{ style: { borderRadius: 0, border: "2px solid #09090B" } }} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <WorkspaceProvider>
              <SettingsProvider>
                <AppShell />
              </SettingsProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
